import React, { useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonLoader from '../../../Components/CommonLoader';
import StatusBadge from '../../../Components/StatusBadge';
import { Body, SubHeading } from '../../../Constants/Fonts';
import {
    risk_portal_ai_categorize,
    risk_portal_ai_duplicates,
    risk_portal_ai_risk_statement,
    risk_portal_ai_suggest_treatment,
} from '../../../services';

// ═══════════════════════════════════════════════════════════════
// FORESEE AI Assist — inline helper embedded in the "Identify Risk" form.
// ---------------------------------------------------------------
// Reads the Risk Description the user has typed (via the CommonModal formRef),
// runs one of the four FORESEE AI analyses, and writes the results straight back
// into the form fields:
//   • Auto-Categorize     → fills Category
//   • Generate ISO Stmt   → fills Description, Threat, Vulnerability, Category,
//                           Likelihood, Impact
//   • Detect Duplicates   → read-only warning list (nothing to fill)
//   • Suggest Treatment   → read-only preview (treatment is a later stage)
// Each response carries a `method` (nova_ai / jaccard / keyword_fallback),
// surfaced as a small "via …" badge so the user sees what answered.
// ═══════════════════════════════════════════════════════════════

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

// ── Icons (inline, stroke-based to match the portal's line-icon set) ──
const svg = (children) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const ICON_SPARK = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2l1.6 4.6a5 5 0 0 0 3.1 3.1L21.3 11l-4.6 1.6a5 5 0 0 0-3.1 3.1L12 20.3l-1.6-4.6a5 5 0 0 0-3.1-3.1L2.7 11l4.6-1.6a5 5 0 0 0 3.1-3.1L12 2z" />
        <path d="M19 3l.5 1.5L21 5l-1.5.5L19 7l-.5-1.5L17 5l1.5-.5L19 3z" opacity="0.85" />
    </svg>
);
const ICON_TAG = svg(<><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0l-7-7a2 2 0 0 1-.6-1.4V5a2 2 0 0 1 2-2h7.2a2 2 0 0 1 1.4.6l7 7a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" /></>);
const ICON_DUP = svg(<><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></>);
const ICON_DOC = svg(<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>);
const ICON_SHIELD = svg(<><path d="M12 3l7 3v5c0 4.5-3 8.3-7 9.5C8 19.3 5 15.5 5 11V6l7-3z" /><path d="M9.5 12l1.8 1.8L15 10" /></>);

const clamp15 = (n) => Math.min(5, Math.max(1, Number(n) || 1));

const similarityColor = (n) => (n > 70 ? 'var(--color-danger)' : n > 50 ? 'var(--color-high)' : 'var(--color-medium)');

// Map an AI-returned category string onto one of the form's actual category
// options (match by value or label, case-insensitively, then a loose contains).
// Returns the option's `value` to set, or null when nothing lines up.
const matchCategory = (cat, options) => {
    const c = String(cat || '').trim().toLowerCase();
    if (!c) return null;
    const opts = (options || []).filter((o) => o.value);
    const exact = opts.find(
        (o) => String(o.value).toLowerCase() === c || String(o.label).toLowerCase() === c
    );
    if (exact) return exact.value;
    const loose = opts.find(
        (o) => String(o.label).toLowerCase().includes(c) || c.includes(String(o.label).toLowerCase())
    );
    return loose ? loose.value : null;
};

// Small labelled value used in the result preview.
const Field = ({ label, children, boxed = false }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Body as="span" variant={2} weight="semibold" color="var(--color-text-muted)" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Body>
        {boxed ? (
            <Body variant={2} color="var(--color-text-primary)" style={{ margin: 0, background: 'var(--color-surface)', border: '1px solid var(--color-border)', padding: 8, borderRadius: 'var(--radius-sm)', lineHeight: 1.55 }}>{children || '—'}</Body>
        ) : (
            <Body as="span" variant={2} color="var(--color-text-secondary)">{children || '—'}</Body>
        )}
    </div>
);

export default function NovaAiAssist({ formApi, categoryOptions = [] }) {
    const [running, setRunning] = useState(null);   // which action is in-flight
    const [result, setResult] = useState(null);     // { type, applied?, ...data }
    const [error, setError] = useState(null);

    const readDescription = () => String(formApi?.current?.getValues?.('risk') || '').trim();
    const setField = (name, value) =>
        formApi?.current?.setValue?.(name, value, { shouldDirty: true, shouldValidate: true });

    const run = async (kind, call, apply) => {
        const description = readDescription();
        setResult(null);
        if (!description) {
            setError('Enter a risk description above first, then run FORESEE AI.');
            return;
        }
        setRunning(kind);
        setError(null);
        try {
            const res = await call(description);
            const d = unwrap(res);
            const innerErr = res?.statusCode?.error?.message || res?.errors?.message;
            if (innerErr) { setError(innerErr); return; }
            if (!d) { setError('FORESEE AI returned an empty response. The service may be busy — try again in a moment.'); return; }
            const applied = apply ? apply(d) : null;
            setResult({ type: kind, applied, ...d });
        } catch (e) {
            setError(`FORESEE AI failed: ${e?.response?.data?.error?.message || e?.message || 'Service unavailable'}`);
        } finally {
            setRunning(null);
        }
    };

    // ── Apply handlers: write AI results into the form, return a summary list ──
    const applyCategorize = (d) => {
        const val = matchCategory(d.category, categoryOptions);
        if (val) { setField('risk_type', val); return [`Category → ${val}`]; }
        return [];
    };

    const applyStatement = (d) => {
        const applied = [];
        if (d.risk_statement) { setField('risk', d.risk_statement); applied.push('Description'); }
        if (d.threat) { setField('threat', d.threat); applied.push('Threat'); }
        if (d.vulnerability) { setField('vulnerability', d.vulnerability); applied.push('Vulnerability'); }
        if (d.suggested_likelihood) { setField('likelihood', clamp15(d.suggested_likelihood)); applied.push('Likelihood'); }
        if (d.suggested_impact) { setField('impact', clamp15(d.suggested_impact)); applied.push('Impact'); }
        const cat = matchCategory(d.suggested_category, categoryOptions);
        if (cat) { setField('risk_type', cat); applied.push('Category'); }
        return applied;
    };

    const ACTIONS = [
        { kind: 'categorize', label: 'Auto-Categorize', icon: ICON_TAG, hint: 'Classify into an ISO category', call: (description) => risk_portal_ai_categorize({ description }), apply: applyCategorize },
        { kind: 'duplicates', label: 'Detect Duplicates', icon: ICON_DUP, hint: 'Find similar existing risks', call: (description) => risk_portal_ai_duplicates({ description }) },
        { kind: 'statement', label: 'Generate ISO Statement', icon: ICON_DOC, hint: 'Fill threat, vuln & scores', call: (description) => risk_portal_ai_risk_statement({ description }), apply: applyStatement },
        {
            kind: 'treatment', label: 'Suggest Treatment', icon: ICON_SHIELD, hint: 'Preview a treatment plan',
            call: (description) => risk_portal_ai_suggest_treatment({
                description,
                risk_type: String(formApi?.current?.getValues?.('risk_type') || ''),
                severity: '',
            }),
        },
    ];

    return (
        <div className="rp1_ai_assist">
            <div className="rp1_ai_assist_head">
                <span className="rp1_ai_spark" aria-hidden="true">{ICON_SPARK}</span>
                <div className="rp1_ai_assist_head_text">
                    <SubHeading variant={2} as="span" color="var(--color-text-primary)" style={{ margin: 0 }}>FORESEE AI Assist</SubHeading>
                    <Body as="span" variant={2} color="var(--color-text-muted)">Analyse your description and auto-fill the form below.</Body>
                </div>
            </div>

            <div className="rp1_ai_grid">
                {ACTIONS.map((a) => {
                    const busy = running === a.kind;
                    return (
                        <button
                            key={a.kind}
                            type="button"
                            className={`rp1_ai_btn${busy ? ' rp1_ai_btn--busy' : ''}`}
                            onClick={() => run(a.kind, a.call, a.apply)}
                            disabled={!!running}
                        >
                            <span className="rp1_ai_btn_icon">{a.icon}</span>
                            <span className="rp1_ai_btn_text">
                                <span className="rp1_ai_btn_label">{busy ? 'Analyzing…' : a.label}</span>
                                <span className="rp1_ai_btn_hint">{a.hint}</span>
                            </span>
                        </button>
                    );
                })}
            </div>

            {running && (
                <div style={{ position: 'relative', minHeight: 44, marginTop: 10 }}>
                    <CommonLoader overlay label="Calling FORESEE AI — typically 2–5 seconds…" />
                </div>
            )}

            {error && !running && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--color-danger-soft)', borderLeft: '3px solid var(--color-danger)' }}>
                    <Body as="span" variant={2} color="var(--color-danger)" style={{ margin: 0 }}>{error}</Body>
                    <CommonButton variant="secondary" text="Dismiss" onClick={() => setError(null)} />
                </div>
            )}

            {result && !running && (
                <div style={{ marginTop: 10, padding: 12, borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', border: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {Array.isArray(result.applied) && result.applied.length > 0 && (
                                <Body as="span" className="rp1_badge rp1_badge--success">Applied: {result.applied.join(', ')}</Body>
                            )}
                        </div>
                        <CommonButton variant="secondary" text="Clear" onClick={() => setResult(null)} />
                    </div>

                    {result.type === 'categorize' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                {result.category && <StatusBadge variant="info" label={result.category} />}
                                {result.confidence != null && <StatusBadge variant="primary" label={`${Math.round((result.confidence || 0) * 100)}% confidence`} />}
                            </div>
                            {(!result.applied || result.applied.length === 0) && (
                                <Body as="span" variant={2} color="var(--color-text-muted)">
                                    “{result.category}” doesn’t map to one of your configured categories — pick the closest one manually.
                                </Body>
                            )}
                            {result.reasoning && <Body variant={2} color="var(--color-text-secondary)" style={{ margin: 0 }}>{result.reasoning}</Body>}
                        </div>
                    )}

                    {result.type === 'statement' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <Field label="Risk Statement" boxed>{result.risk_statement}</Field>
                            <div className="rp1_nova_grid2">
                                <Field label="Threat">{result.threat}</Field>
                                <Field label="Vulnerability">{result.vulnerability}</Field>
                            </div>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                                {result.suggested_category && <StatusBadge variant="info" label={result.suggested_category} />}
                                <Body as="span" variant={2} color="var(--color-text-secondary)">Likelihood: <Body as="span" variant={2} weight="bold" color="var(--color-text-primary)">{clamp15(result.suggested_likelihood)}/5</Body></Body>
                                <Body as="span" variant={2} color="var(--color-text-secondary)">Impact: <Body as="span" variant={2} weight="bold" color="var(--color-text-primary)">{clamp15(result.suggested_impact)}/5</Body></Body>
                            </div>
                        </div>
                    )}

                    {result.type === 'duplicates' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Body as="span" variant={2} weight="semibold" color="var(--color-text-primary)">
                                {result.count || 0} potential duplicate{(result.count || 0) === 1 ? '' : 's'} found
                            </Body>
                            {(result.duplicates || []).length === 0 ? (
                                <Body as="span" variant={2} color="var(--color-text-muted)">No similar risks in the registry — you’re good to create this one.</Body>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {(result.duplicates || []).map((d, i) => (
                                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', paddingBottom: 6, borderBottom: '1px solid var(--color-border)' }}>
                                            <Body as="span" variant={2} weight="semibold" color="var(--color-primary)">{d.risk_id || '—'}</Body>
                                            <Body as="span" variant={2} weight="bold" color={similarityColor(d.similarity)}>{d.similarity}%</Body>
                                            <Body as="span" variant={2} color="var(--color-text-secondary)" style={{ flex: '1 1 200px', minWidth: 0 }}>{d.risk || '—'}</Body>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {result.type === 'treatment' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {result.recommended_strategy && <StatusBadge variant="success" label={result.recommended_strategy} />}
                                {result.cost_estimate && <StatusBadge variant="neutral" label={`Cost: ${result.cost_estimate}`} />}
                                {result.timeline && <StatusBadge variant="neutral" label={`Timeline: ${result.timeline}`} />}
                            </div>
                            <Field label="Suggested Treatment Plan" boxed>{result.treatment_plan}</Field>
                            <Body as="span" variant={2} color="var(--color-text-muted)">
                                Treatment is captured later in the workflow — this preview is here to inform the plan.
                            </Body>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
